import type { DriveStep } from 'driver.js';

export const PORTAL_PDF_MARKUP_TOUR_SELECTORS = {
  modeBar: '[data-tour="portal-pdf-mode-bar"]',
  modeComment: '[data-tour="portal-pdf-mode-comment"]',
  modeDraw: '[data-tour="portal-pdf-mode-draw"]',
  drawTools: '[data-tour="portal-pdf-draw-tools"]',
  canvas: '[data-tour="portal-pdf-canvas"]',
  pagination: '[data-tour="portal-pdf-pagination"]',
  help: '[data-tour="portal-pdf-help"]',
} as const;

export interface PortalPdfMarkupTourStepOptions {
  hasPagination: boolean;
}

export function buildPortalPdfMarkupTourSteps(opts: PortalPdfMarkupTourStepOptions): DriveStep[] {
  const intro: DriveStep = {
    popover: {
      title: 'Review & mark up your drawings',
      description:
        'Use this viewer to leave comments and freehand markup on your engineered drawings. Here is a quick walkthrough.',
      align: 'center',
    },
  };

  const commentStep: DriveStep = {
    element: PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeComment,
    popover: {
      title: 'Comments',
      description:
        'With <strong>Comments</strong> selected, click anywhere on the drawing to pin a note. Your team will see your feedback and can reply in the thread.',
      side: 'bottom',
      align: 'start',
    },
  };

  const drawModeStep: DriveStep = {
    element: PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw,
    popover: {
      title: 'Draw on the PDF',
      description:
        'Switch to <strong>Draw</strong> to sketch with a pen or highlighter. Pick a color and mark up the sheet directly.',
      side: 'bottom',
      align: 'start',
    },
  };

  const drawToolsStep: DriveStep = {
    element: PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools,
    popover: {
      title: 'Pen, highlighter, and colors',
      description:
        'Choose pen or highlighter, then pick a color. Use the eraser to remove strokes you added.',
      side: 'bottom',
      align: 'start',
    },
  };

  const canvasStep: DriveStep = {
    element: PORTAL_PDF_MARKUP_TOUR_SELECTORS.canvas,
    popover: {
      title: 'The drawing canvas',
      description:
        'Comments and markup stay on the PDF as you zoom and navigate. Existing pins and strokes from your team appear here too.',
      side: 'top',
      align: 'center',
    },
  };

  const paginationStep: DriveStep = {
    element: PORTAL_PDF_MARKUP_TOUR_SELECTORS.pagination,
    popover: {
      title: 'Multi-page drawings',
      description: 'Use Previous and Next to move between pages. Comments and markup are saved per page.',
      side: 'top',
      align: 'center',
    },
  };

  const outro: DriveStep = {
    popover: {
      title: 'You are all set',
      description:
        'Leave comments where something needs clarification and use draw tools to highlight areas of interest. Press Escape or the X button to close the viewer when you are done.',
      align: 'center',
    },
  };

  const steps: DriveStep[] = [intro, commentStep, drawModeStep, drawToolsStep, canvasStep];

  if (opts.hasPagination) {
    steps.push(paginationStep);
  }

  steps.push(outro);
  return steps;
}
